import { Injectable } from '@angular/core';
import {
    HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpErrorResponse, HttpClient
} from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { APIError } from '@shared/helpers/constants';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private badRequestCode = 400;
    private unauthorized = 401;
    private responseMessage: string;
    private responseErrorCode: string;
    private errorCodeLocal: number;
    session: any;
    decodedIdToken: string;
    errMsg: string;
  constructor(
  private http: HttpClient,
    private authService: AuthService,
    private route: Router,
    private translateService: TranslateService
  ) { }
  getCognitoAccessTokenFromStorage(): string {
    let cognitoAccessToken: any;
    if (!!this.session) {
      cognitoAccessToken = this.session.getAccessToken().getJwtToken();
    } else {
      for (const key in localStorage) {
        if (key.includes('clpSession')) {
          const session = JSON.parse(localStorage.getItem('clpSession') || '{}');
          cognitoAccessToken = session.accessToken;
        }
      }
    }
    return cognitoAccessToken;
  }
  /**
     * (description): decode idtoken
     * (parameter) : none
     * (returns) : callback
     * (memberof) : JwtInterceptor
     */
  decode(idToken?: string): any {
    if (!!idToken && idToken !== undefined) {
      this.decodedIdToken = jwt_decode(idToken);
    }
    return this.decodedIdToken;
  }

  /**
* (description): refreshtoken
* (parameter) : none
* (returns) : callback
* (memberof) : JwtInterceptor
*/
  public async refreshToken(): Promise<any> {   
    let accessToken = '';
    return await new Promise((resolve, reject) => {
      const accessTokenFromStorage = this.getCognitoAccessTokenFromStorage();
      const decodedToken = this.decode(accessTokenFromStorage);
      const accessTokenExpire = decodedToken ? decodedToken.exp : '';
      const expired = Date.now() >= accessTokenExpire * 1000;
      if (!expired && accessTokenFromStorage) {
        resolve(accessTokenFromStorage);
      } else {
        const session = JSON.parse(localStorage.getItem('clpSession')|| '{}');        
        if (localStorage.getItem('clpSession') == null) {
          resolve('');
        } else {
          this.refreshTokenCode(session.refresh_token).then((data) => {
            session.accessToken = data.access_token;
            session.idToken = data.id_token;
            localStorage.setItem('clpSession', JSON.stringify(session));
            resolve(data.access_token);
          }).catch((error) => {
            this.handleError(error);
          });
        }
      }
    });
  }


  /**
* (description): refreshtoken
* (parameter) : code
* (returns) : callback
* (memberof) : JwtInterceptor
*/
  async refreshTokenCode(code): Promise<any> {
    const test = `grant_type=refresh_token&client_id=${environment.userPoolWebClientId}&refresh_token=${code}`;
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    return await this.http.post(environment.hostedUIUrl + 'token', test, { headers }).pipe(catchError(this.handleError)).toPromise();

  }

  /**
* (description): S3 Get File Functionality
* (parameter) : file
* (returns) : ERROR
* (memberof) : JwtInterceptor
*/
  private handleError(error: HttpErrorResponse): any {
    if (error.error instanceof ErrorEvent) {
      this.errMsg = 'An error occurred:' + error.error.message;
    } else {
      const statusCode = 400;
      if (error.status === statusCode) {
        this.errMsg = error.error.errorMessage;
      } else {
        this.errMsg = 'Technical error occurred'
      }
    }
    return throwError(this.errMsg);
  }
  /**
   * This method is to intercept the HTTP request to set Authorization token
   *
   * (parameter) request: HttpRequest<any>
   * (parameter) next: HttpHandler<any>
   * (returns) : Observable<HttpEvent<any>>
   * (memberof) : JwtInterceptor
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // ToDO: Need to implement
    // add authorization header with jwt token if available
    let header: any = ' ';
    const auditLogActor: any = '';
    let urlAdd: any = '';

    this.translateService.get(['errorCode']).subscribe((data) => {
      this.errorCodeLocal = data.errorCode;
    });

    // add authorization header with jwt token if available
    let userAccessToken: any = '';
    if(request.url === environment.hostedUIUrl + 'token'){
            return next.handle(request)
                    .pipe(
                        tap(event => {
                            if (event instanceof HttpResponse) {
                                /* Intentional Empty Block */
                            }
                        }, (error: HttpErrorResponse) => {
                            if (error instanceof HttpErrorResponse) {
                                this.responseMessage = error.error ? error.error.message : '';
                                this.responseErrorCode = error.error ? error.error.errorCode : '';
                                if (this.errorCodeLocal.hasOwnProperty(error.error.errorCode)) {
                                    error.error.errorMessage = this.errorCodeLocal[error.error.errorCode];
                                }
                                 else if (error.status === this.unauthorized || error.status === 0) {
                                  this.authService.signOut();     
                                }
                            }
                        })
                    );
        }
    return from(this.refreshToken()).pipe(
      switchMap((tokenResult) => {
        userAccessToken = tokenResult;
        const bucket = request.url.split('/')[2].split('.')[0];
        if (
          !!userAccessToken &&
          bucket !== environment.bucketForMask &&
          bucket !== environment.bucketForImage && 
          bucket !== environment.bucketForTibiaMask &&
          bucket !== environment.bucketForTibiaImage
        ) {
          header = {
            setHeaders: {
              Authorization: `${userAccessToken}`,
            },
          };  
          request = request.clone(header);
          // code to stop IE from caching the requests
          urlAdd = 'buster=' + new Date().getTime();
          if (request.url.includes('?')) {
            urlAdd = '&' + urlAdd;
          } else {
            urlAdd = '?' + urlAdd;
          }
          const newUrl = { url: request.url + urlAdd };
          request = request.clone(newUrl);
        }
        return next.handle(request).pipe(
          tap(
            (event) => {
              if (event instanceof HttpResponse) {
                // Handle Http Response

                if(event.url?.includes('/exportselectedlog')){
                  if(event.body == ""){

                    this.authService.showAPIErrorMsg(APIError.NoAuditLogs);
                  }
                }
              }
            },
            (error: HttpErrorResponse) => {
              if (error instanceof HttpErrorResponse) {             
                if (this.errorCodeLocal.hasOwnProperty(error.error.errorCode)) {
                  error.error.errorMessage =
                    this.errorCodeLocal[error.error.errorCode];
                }
                if (error.status === this.unauthorized || error.status === 0) {
                    this.authService.signOut();
                }else{
                  if(request.url?.includes('/casedashboard') 
                    || request.url?.includes('/updatecasedetails')
                    || request.url?.includes('/duedate')
                    || request.url?.includes('/exportlog')
                    || request.url?.includes('/exportselectedlog') || request.url?.includes('/downloadauditlog') || request.url?.includes('/auditlogmetadata')){
                    this.authService.showAPIErrorMsg(APIError.processingErr);
                  }                 
                }
              }
            }
          )
        );
      })
    );
  }
}
